import { useCallback, useEffect, useRef, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import {
  TiendaPerfectaVisitHistoryParams,
  TiendaPerfectaVisitHistoryStop,
  TiendaPerfectaVisitHistoryStopsResponse,
  TiendaPerfectaVisitHistorySummary,
} from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

import { visitHistoryQueryKey } from '../utils/queryKey';
import { ERROR_TOAST_SECONDS } from '../utils/toast';

interface UseVisitHistoryReturn {
  /** First load of a query: the one that shows skeletons. */
  loading: boolean;
  /** Background reload: keeps what is on screen. */
  refreshing: boolean;
  summary: TiendaPerfectaVisitHistorySummary | null;
  stops: TiendaPerfectaVisitHistoryStop[];
  lastUpdatedAt: Date | null;
  /** A load the user was waiting on failed and left nothing to show. */
  error: boolean;
  refresh: () => void;
}

/**
 * The range summary and its flat stops, loaded in parallel. Without `params`
 * nothing is requested (the scope gate has not opened yet).
 *
 * Every failure is told to the user (`message.error`, project rule). A failed
 * refresh keeps the data already on screen; a failed first load leaves the
 * error state so the screen never looks like it is still loading.
 */
export function useVisitHistory(
  params: TiendaPerfectaVisitHistoryParams | undefined
): UseVisitHistoryReturn {
  const { message } = App.useApp();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] =
    useState<TiendaPerfectaVisitHistorySummary | null>(null);
  const [stops, setStops] = useState<TiendaPerfectaVisitHistoryStop[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState(false);

  const queryKey = visitHistoryQueryKey(params) || null;
  const paramsRef = useRef(params);
  paramsRef.current = params;
  // Kept in refs so a new identity never re-triggers the fetch effect.
  const messageRef = useRef(message);
  messageRef.current = message;
  const fallbackText = useRef('');
  fallbackText.current = t('scenes.visit_history.error.fallback');
  /** The newest request wins; an older answer no longer matches the filters. */
  const requestId = useRef(0);
  const active = useRef(true);
  const inFlight = useRef(false);
  const hasError = useRef(false);

  const load = useCallback(async (initial: boolean) => {
    const current = paramsRef.current;
    if (!current) return;

    requestId.current += 1;
    const id = requestId.current;
    inFlight.current = true;

    if (initial) {
      setLoading(true);
      setError(false);
      hasError.current = false;
    } else {
      setRefreshing(true);
    }

    const fail = (text: string) => {
      messageRef.current.error(
        text || fallbackText.current,
        ERROR_TOAST_SECONDS
      );
      if (initial) {
        setSummary(null);
        setStops([]);
        setError(true);
        hasError.current = true;
      }
    };

    try {
      const [summaryResponse, stopsResponse] = await Promise.all([
        TiendaPerfectaService.getVisitHistorySummary(current),
        TiendaPerfectaService.getVisitHistoryStops(current),
      ]);

      if (!active.current || id !== requestId.current) return;

      if (summaryResponse.success && stopsResponse.success) {
        setSummary(summaryResponse.data as TiendaPerfectaVisitHistorySummary);
        setStops(
          (stopsResponse.data as TiendaPerfectaVisitHistoryStopsResponse)
            ?.stops ?? []
        );
        setLastUpdatedAt(new Date());
        setError(false);
        hasError.current = false;
      } else {
        const failed = summaryResponse.success
          ? stopsResponse
          : summaryResponse;
        fail(((failed.data as ApiErrorData)?.message as string) || '');
      }
    } catch {
      // A rejected call must leave the same trace as a `success: false` one.
      if (active.current && id === requestId.current) fail('');
    } finally {
      if (active.current && id === requestId.current) {
        inFlight.current = false;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    active.current = true;
    if (queryKey) load(true);

    return () => {
      active.current = false;
    };
  }, [queryKey, load]);

  const refresh = useCallback(() => {
    // Clicks while a request is in flight must not stack requests. Retrying
    // from the error state is a first load again: it shows progress.
    if (inFlight.current) return;
    load(hasError.current);
  }, [load]);

  return { loading, refreshing, summary, stops, lastUpdatedAt, error, refresh };
}
